import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Mail, AlertCircle, Clock, User, Paperclip, ExternalLink, Download, FileText } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface EmailAccount {
  id: string;
  name: string;
  email: string;
  isPrimary: boolean;
  isActive: boolean;
}

interface EmailMessage {
  messageId: string;
  uid?: number;
  subject: string;
  from: { text: string; value: Array<{ address: string; name?: string }> };
  date: Date;
  text?: string;
  html?: string;
  projectId?: string | null;
  attachments?: Array<{ filename: string; size: number }>;
}

interface AccountMessages {
  accountId: string;
  accountName: string;
  messages: EmailMessage[];
  error?: string;
}

export function EmailMessagesSection() {
  const { toast } = useToast();
  const [refreshingAccount, setRefreshingAccount] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<{ accountId: string; uid: number } | null>(null);
  const [emailDetailOpen, setEmailDetailOpen] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState<number | null>(null);

  const { data: emailAccounts, isLoading: accountsLoading } = useQuery<EmailAccount[]>({
    queryKey: ['/api/email-accounts'],
  });

  const { data: accountMessages, isLoading: messagesLoading, refetch: refetchMessages } = useQuery<AccountMessages[]>({
    queryKey: ['/api/email-messages'],
    enabled: !!emailAccounts && emailAccounts.length > 0,
  });

  const { data: emailDetail, isLoading: emailDetailLoading } = useQuery({
    queryKey: ['/api/email-detail', selectedEmail?.accountId, selectedEmail?.uid],
    enabled: !!selectedEmail,
    queryFn: async () => {
      if (!selectedEmail) return null;
      const response = await apiRequest('GET', `/api/email-accounts/${selectedEmail.accountId}/email/${selectedEmail.uid}`);
      return response.json();
    }
  });

  const refreshAccountMessages = async (accountId: string) => {
    try {
      setRefreshingAccount(accountId);
      await apiRequest('POST', `/api/email-accounts/${accountId}/refresh`);
      await refetchMessages();
    } catch (error) {
      console.error('Error refreshing messages:', error);
      toast({
        title: "Error",
        description: "Failed to refresh email messages. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRefreshingAccount(null);
    }
  };

  const openEmailDetail = (accountId: string, uid: number) => {
    setSelectedEmail({ accountId, uid });
    setEmailDetailOpen(true);
  };

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-400">Loading email accounts...</div>
      </div>
    );
  }

  if (!emailAccounts || emailAccounts.length === 0) {
    return (
      <div className="text-center p-8">
        <Mail className="w-12 h-12 text-gray-500 mx-auto mb-4" />
        <p className="text-gray-400 mb-4">No email accounts configured</p>
        <p className="text-sm text-gray-500">
          Use the "E-Mail Setup" button above to add email accounts and view messages here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-100">Recent Messages</h3>
        <Button
          onClick={() => refetchMessages()}
          disabled={messagesLoading}
          size="sm"
          variant="outline"
          className="border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${messagesLoading ? 'animate-spin' : ''}`} />
          Refresh All
        </Button>
      </div>

      {messagesLoading ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-gray-400">Loading messages...</div>
        </div>
      ) : (
        <div className="space-y-4">
          {emailAccounts.map((account) => {
            const accountData = accountMessages?.find(am => am.accountId === account.id);
            const isRefreshing = refreshingAccount === account.id;

            return (
              <Card key={account.id} className="navy-card border-gray-600">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CardTitle className="text-gray-100 text-base">{account.name}</CardTitle>
                      <div className="flex space-x-2">
                        {account.isPrimary && (
                          <Badge variant="outline" className="border-blue-500 text-blue-400">
                            Primary
                          </Badge>
                        )}
                        <Badge 
                          variant="outline" 
                          className={account.isActive ? "border-green-500 text-green-400" : "border-red-500 text-red-400"}
                        >
                          {account.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                    <Button
                      onClick={() => refreshAccountMessages(account.id)}
                      disabled={isRefreshing || !account.isActive}
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  <div className="text-sm text-gray-400">{account.email}</div>
                </CardHeader>
                <CardContent>
                  {accountData?.error ? (
                    <div className="flex items-center space-x-2 text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Error: {accountData.error}</span>
                    </div>
                  ) : accountData?.messages && accountData.messages.length > 0 ? (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-400 mb-3">
                        {accountData.messages.length} email{accountData.messages.length !== 1 ? 's' : ''} found
                      </div>
                      
                      {/* Scrollable email list - one line per email */}
                      <div className="max-h-96 overflow-y-auto space-y-1 pr-2">
                        {accountData.messages.map((message, index) => (
                          <div 
                            key={message.messageId || index} 
                            className="flex items-center justify-between border border-gray-600 rounded-lg px-3 py-2 bg-gray-800/30 hover:bg-gray-800/50 transition-colors cursor-pointer group"
                            onClick={() => openEmailDetail(account.id, message.uid || 0)}
                          >
                            {/* Left side: UID, Project Assignment and Subject */}
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <span className="font-mono bg-gray-700 px-2 py-1 rounded text-yellow-400 text-xs flex-shrink-0">
                                UID: {message.uid || 'N/A'}
                              </span>
                              {message.projectId ? (
                                <span className="font-mono bg-blue-900 px-2 py-1 rounded text-blue-300 text-xs flex-shrink-0">
                                  {message.projectId}
                                </span>
                              ) : (
                                <span className="font-mono bg-orange-900 px-2 py-1 rounded text-orange-400 text-xs flex-shrink-0">
                                  NOT ASSIGNED
                                </span>
                              )}
                              <span className="text-sm text-gray-200 truncate flex-1">
                                {message.subject || '(No Subject)'}
                              </span>
                            </div>
                            
                            {/* Right side: Sender email and click indicator */}
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              <span className="text-xs text-gray-400 font-mono">
                                {message.from?.value?.[0]?.address || message.from?.text || 'Unknown sender'}
                              </span>
                              <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-blue-400 transition-colors" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">
                      {account.isActive ? 'No messages found' : 'Account inactive - cannot fetch messages'}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Email Detail Dialog */}
      <Dialog open={emailDetailOpen} onOpenChange={setEmailDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden navy-card">
          <DialogHeader>
            <DialogTitle className="text-gray-100 flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <span>Email Details</span>
              {selectedEmail && (
                <span className="font-mono text-sm text-yellow-400 ml-2">
                  UID: {selectedEmail.uid}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {emailDetailLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="text-gray-400">Loading email...</div>
              </div>
            ) : emailDetail ? (
              <div className="space-y-4">
                {/* Email Headers */}
                <div className="border-b border-gray-600 pb-4">
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400 font-medium">Subject: </span>
                      <span className="text-gray-200">{emailDetail.subject || '(No Subject)'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 font-medium">From: </span>
                      <span className="text-gray-200 font-mono">
                        {emailDetail.from?.value?.[0]?.name ? 
                          `${emailDetail.from.value[0].name} <${emailDetail.from.value[0].address}>` : 
                          emailDetail.from?.value?.[0]?.address || emailDetail.from?.text || 'Unknown sender'
                        }
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400 font-medium">Date: </span>
                      <span className="text-gray-200">
                        {emailDetail.date ? new Date(emailDetail.date).toLocaleString() : 'Unknown date'}
                      </span>
                    </div>
                    {emailDetail.to && (
                      <div>
                        <span className="text-gray-400 font-medium">To: </span>
                        <span className="text-gray-200 font-mono">
                          {Array.isArray(emailDetail.to.value) ? 
                            emailDetail.to.value.map((addr: any) => addr.address).join(', ') : 
                            emailDetail.to.text
                          }
                        </span>
                      </div>
                    )}
                    {emailDetail.attachments && emailDetail.attachments.length > 0 && (
                      <div>
                        <span className="text-gray-400 font-medium">Attachments: </span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {emailDetail.attachments.map((attachment: any, idx: number) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className={`border-blue-500 text-blue-400 hover:bg-blue-500/20 cursor-pointer transition-colors ${
                                downloadingAttachment === idx ? 'opacity-50' : ''
                              }`}
                              onClick={async () => {
                                try {
                                  setDownloadingAttachment(idx);
                                  const downloadUrl = `/api/email-accounts/${selectedEmail?.accountId}/email/${selectedEmail?.uid}/attachment/${idx}`;
                                  
                                  // Fetch the attachment
                                  const response = await fetch(downloadUrl);
                                  if (!response.ok) {
                                    throw new Error('Failed to download attachment');
                                  }
                                  
                                  // Create blob and download
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = attachment.filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                } catch (error) {
                                  console.error('Download failed:', error);
                                  toast({
                                    title: "Download Failed",
                                    description: "Failed to download attachment. Please try again.",
                                    variant: "destructive",
                                  });
                                } finally {
                                  setDownloadingAttachment(null);
                                }
                              }}
                            >
                              <Paperclip className="w-3 h-3 mr-1" />
                              {downloadingAttachment === idx ? 'Downloading...' : `${attachment.filename} (${Math.round(attachment.size / 1024)}KB)`}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Email Content */}
                <div className="space-y-4">
                  {emailDetail.html ? (
                    <div className="border border-gray-600 rounded-lg bg-white">
                      <iframe
                        srcDoc={emailDetail.html}
                        className="w-full h-96 rounded-lg"
                        sandbox="allow-same-origin"
                        title="Email Content"
                      />
                    </div>
                  ) : emailDetail.text ? (
                    <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
                      <pre className="whitespace-pre-wrap text-sm text-gray-200 font-mono">
                        {emailDetail.text}
                      </pre>
                    </div>
                  ) : (
                    <div className="text-center p-8 text-gray-500">
                      No content available
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-8 text-gray-500">
                Failed to load email details
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}